import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import { getProductBoxDefaultImageDim } from "../../../common/functions";
import { ITheme } from "@/api/AppApi/services/company/interfaces/ICompany";
import ProductBadge from "./ProductBadge";
import type { IResolvedProductTag } from "@/utils/functions/product-tags";
import React, { useMemo } from "react";
import ProductBoxImageSlider, { MAX_CARD_SLIDES } from "./ProductBoxImageSlider";
import {
  DiscountTypes,
  IProduct,
} from "@/api/AppApi/services/home/interfaces/IProduct";

interface Props {
  imgWidth?: number;
  imgHeight?: number;
  className: string;
  src: string;
  is_new: boolean;
  size: Size;
  discount_amount: number;
  discount_type?: DiscountTypes;
  has_discount: boolean;
  priority: boolean;
  discount?: number;
  theme: ITheme | undefined;
  images?: IProduct["images"];
  outOfStock?: boolean;
  sold_quantity?: number;
  overlay?: React.ReactNode;
  image?: IProduct["images"][0];
  /** Only the product card passes these; cart/order rows render no tag badges. */
  productTags?: IResolvedProductTag[];
  productId?: number | string;
}

const ProductImg: React.FC<Props> = ({
  className,
  src,
  size,
  is_new,
  has_discount,
  discount_type,
  discount_amount,
  imgWidth,
  imgHeight,
  discount,
  theme,
  images,
  outOfStock,
  sold_quantity,
  overlay,
  image,
  priority,
  productTags,
  productId,
}) => {
  const {
    show_discount_badge,
    show_new_badge,
    sale_badge_style,
    show_sold_quantity_badge,
  } = theme?.components?.product ?? {};

  const showDiscountBadge = show_discount_badge && has_discount;

  const showNewBadge = show_new_badge && is_new;

  const showSoldQuantityBadge =
    !!show_sold_quantity_badge && !!sold_quantity && sold_quantity > 0;

  const isSaleLabel = sale_badge_style === "label" && showDiscountBadge;

  const image_src = useMemo(() => {
    if (!image) return src;
    return image.thumbnail_url ?? image.image_url ?? src;
  }, [image, src]);

  // The card's image sequence, in the product's own order and capped at 5.
  //
  // Built from `image_src` rather than `images[0]` on purpose: for a configurable
  // product `src` is the SELECTED variant's own photo, which need not appear in
  // `images` at all. Starting there keeps the default slide equal to what the card
  // shows today, and the URL de-dup then drops the same entry the old hover filter
  // did (`image.thumbnail_url !== src`) without ever repeating an image.
  //
  // Videos are dropped: a <video> per card is not affordable on a listing page, and
  // a poster-only slide the shopper cannot play is worse than no slide.
  const slides = useMemo(() => {
    const urls: string[] = [];
    const push = (url?: string | null) => {
      if (!url || urls.length >= MAX_CARD_SLIDES || urls.includes(url)) return;
      urls.push(url);
    };
    push(image_src);
    for (const img of images ?? []) {
      if (img?.type === "video") continue;
      push(img.thumbnail_url ?? img.image_url);
    }
    return urls.length ? urls : [image_src];
  }, [image_src, images]);

  const objectFit = theme?.components?.product.product_image_object_fit
    ? theme?.components?.product.product_image_object_fit
    : "cover";
  const imgSizes = [Size.large, Size.vertical_large].includes(size)
    ? "268px"
    : "148px";

  return (
    <div className={className} style={{ position: "relative" }} dir="ltr">
      <ProductBoxImageSlider
        slides={slides}
        alt="product"
        priority={priority}
        objectFit={objectFit}
        sizes={imgSizes}
        aspectPadding={`max(${getProductBoxDefaultImageDim(size).height}px,100%)`}
        imgWidth={imgWidth}
        imgHeight={imgHeight}
      />
      {overlay && (
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
          }}
        >
          {overlay}
        </div>
      )}
      {(showNewBadge ||
        showDiscountBadge ||
        outOfStock ||
        showSoldQuantityBadge ||
        !!productTags?.length) && (
        <ProductBadge
          discount={discount}
          discount_amount={discount_amount}
          size={size}
          discount_type={discount_type}
          showDiscountBadge={showDiscountBadge!}
          showNewBadge={showNewBadge!}
          isSaleLabel={isSaleLabel!}
          outOfStock={outOfStock}
          showSoldQuantityBadge={showSoldQuantityBadge}
          sold_quantity={sold_quantity}
          productTags={productTags}
          productId={productId}
        />
      )}
    </div>
  );
};

export default React.memo(ProductImg);
